import { Chip } from "$components/chip";
import { ErrorBoundary } from "$components/error-boundary";
import { Table } from "$components/table";
import { toast } from "$global-providers/toast-provider";
import { gql, useApolloClient } from "@apollo/client";
import { Box, Typography } from "@material-ui/core";
import { format } from "date-fns";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  type RegisterDownloadRequest,
  RegisterDownloadStatus,
  RegisterType,
} from "../../../__generated__/base.gql";
import { paginationDefaults } from "../_lib/constant";
import { isDefined } from "../_lib/is-defined";
import {
  DownloadRegisterResultDocument,
  type DownloadRegisterResultQuery,
  useFetchRegisterDownloadRequestsQuery,
} from "./__generated__/page.gql";
import { download } from "./_lib/download";
import PasswordModal from "./_lib/password-modal";
import { RegisterDownloadModal } from "./_lib/register-download-modal";

gql`
  query FetchRegisterDownloadRequests($input: RegisterDownloadRequestsInput!) {
    registerDownloadRequests(input: $input) {
      limit
      offset
      requests {
        ...DownloadRequestFragment
      }
    }
  }

  fragment DownloadRequestFragment on RegisterDownloadRequest {
    id
    registerType
    clinic {
      name
    }
    actor {
      person {
        firstname
        lastname
      }
    }
    filters {
      startDate
      endDate
    }
    selectedColumns
    meta {
      created
      updated
    }
    download {
      id
      status
      result {
        expiresAt
      }
    }
  }
`;

gql`
  query DownloadRegisterResult($id: ID!) {
    registerDownloadRequest(id: $id) {
      download {
        result {
          expiresAt
          password
          records
        }
      }
    }
  }
`;

const getRegisterName = (type: RegisterType) => {
  switch (type) {
    case RegisterType.Anc:
      return "ANC";
    case RegisterType.NigeriaDailyOutpatient:
      return "Daily Outpatient Register";
    case RegisterType.NigeriaGeneralAttendance:
      return "General Attendance Register";
    default:
      return "Patient Register";
  }
};
export const RegisterDownloadRequestsPage: FC = () => {
  const { formatMessage } = useIntl();
  const { data, loading } = useFetchRegisterDownloadRequestsQuery({
    variables: { input: { paginationOptions: paginationDefaults } },
    pollInterval: 10000,
  });
  const [isOpen, setOpen] = React.useState(false);
  const [password, setPassword] = React.useState<string | undefined>(undefined);
  const apolloClient = useApolloClient();
  const [registerToDownload, setRegisterToDownload] = React.useState();

  const execQuery = React.useCallback(
    (req: RegisterDownloadRequest) => async () => {
      try {
        setPassword(undefined);
        const { data: dlData } =
          await apolloClient.query<DownloadRegisterResultQuery>({
            query: DownloadRegisterResultDocument,
            variables: { id: req.id },
            fetchPolicy: "no-cache",
          });

        if (
          dlData?.registerDownloadRequest.download?.result?.records &&
          dlData?.registerDownloadRequest.download?.result?.password
        ) {
          try {
            download(
              `[Confidential] [${req.clinic.name}] [${getRegisterName(
                req.registerType,
              )}] ${format(
                new Date(req.filters.startDate),
                "dd MMM yyyy",
              )} to ${format(new Date(req.filters.endDate), "dd MMM yyyy")}`,
              dlData.registerDownloadRequest.download.result.records,
            );
            setOpen(false);
            setPassword(
              dlData.registerDownloadRequest.download.result.password,
            );
          } catch (_e) {
            toast.error(
              formatMessage({
                id: "register-requests.download.file.error.toast",
                defaultMessage: "Failed to write register file to storage",
              }),
            );
          }
        }
      } catch (_error) {
        toast.error(
          formatMessage({
            id: "register-requests.download.network.error.toast",
            defaultMessage: "Failed to download register file over network.",
          }),
        );
      }
    },
    [apolloClient, formatMessage],
  );
  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          flexDirection: "row",
          display: "flex",
          my: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h3">
          <FormattedMessage
            id="register-requests.title"
            defaultMessage="Register Download Requests"
          />
        </Typography>
      </Box>
      <ErrorBoundary name="register-requests-table">
        <Table
          hideHeader={false}
          stickyHeader
          columns={[
            {
              field: "actorName",
              headerName: formatMessage({
                id: "register-requests.table.column.actorName",
                defaultMessage: "Requested by",
              }),
              flex: 1,
            },
            {
              field: "registerType",
              headerName: formatMessage({
                id: "register-requests.table.column.registerType",
                defaultMessage: "Register type",
              }),
              flex: 1,
            },
            {
              field: "status",
              headerName: formatMessage({
                id: "register-requests.table.column.status",
                defaultMessage: "Status",
              }),
              flex: 1,
              renderCell: (value: RegisterDownloadStatus) => {
                const colors: Record<
                  RegisterDownloadStatus,
                  React.ComponentProps<typeof Chip>["color"]
                > = {
                  [RegisterDownloadStatus.Complete]: "success",
                  [RegisterDownloadStatus.Error]: "error",
                  [RegisterDownloadStatus.Processing]: "primary",
                  [RegisterDownloadStatus.Expired]: "disabled",
                };
                return <Chip label={value} color={colors[value]} />;
              },
            },
            {
              field: "requestedAt",
              headerName: formatMessage({
                id: "register-requests.table.column.requestedAt",
                defaultMessage: "Requested at",
              }),
              flex: 1,
            },
            {
              field: "startDate",
              headerName: formatMessage({
                id: "register-requests.table.column.startDate",
                defaultMessage: "Start date",
              }),
              flex: 1,
            },
            {
              field: "endDate",
              headerName: formatMessage({
                id: "register-requests.table.column.endDate",
                defaultMessage: "End date",
              }),
              flex: 1,
            },
            {
              field: "columns",
              headerName: formatMessage({
                id: "register-requests.table.column.columns",
                defaultMessage: "Columns",
              }),
              flex: 1,
            },
            {
              field: "updated",
              headerName: formatMessage({
                id: "register-requests.table.column.updated",
                defaultMessage: "Last updated at",
              }),
              flex: 1,
            },
          ]}
          rows={
            data?.registerDownloadRequests.requests
              .filter(isDefined)
              .map((r) => ({
                id: r.id,
                actorName: `${r.actor.person?.firstname} ${r.actor.person?.lastname}`,
                registerType: getRegisterName(r.registerType),
                status: r.download?.status,
                requestedAt: format(
                  new Date(r.meta.created),
                  "dd/MM/yyyy HH:mm:ss",
                ),
                startDate: format(new Date(r.filters.startDate), "dd/MM/yyyy"),
                endDate: format(new Date(r.filters.endDate), "dd/MM/yyyy"),
                columns: r.selectedColumns?.join(", ") ?? "-",
                updated: format(
                  new Date(r.meta.updated),
                  "dd/MM/yyyy HH:mm:ss",
                ),
                _original: r,
              })) ?? []
          }
          noDataMessage={
            loading ? (
              <Box>
                <Typography variant="subtitle1">
                  <FormattedMessage
                    id="register-requests.loading"
                    defaultMessage="Fetching Register Download Requests..."
                  />
                </Typography>
              </Box>
            ) : (
              <Box style={{ textAlign: "left" }}>
                <Typography variant="subtitle1">
                  <FormattedMessage
                    id="register-requests.empty"
                    defaultMessage="There are no register download requests"
                  />
                </Typography>
              </Box>
            )
          }
          onRowClick={(row) => {
            if (row.status === RegisterDownloadStatus.Complete) {
              setRegisterToDownload(row._original);
              setOpen(true);
            }
          }}
        />
      </ErrorBoundary>
      <RegisterDownloadModal
        open={isOpen}
        onConfirm={execQuery(registerToDownload)}
        onCancel={() => setOpen(false)}
      />
      <PasswordModal password={password} />
    </>
  );
};
